import React, { useId } from 'react';
import {
  formatEventOption,
  emptyEventsMessage,
  findBanquetEvent,
  monthBoundaryNote,
} from '../utils/banquetWastageForm';

/**
 * The wastage screen's required "Banquet event" field, shown only while the
 * banquet reason is selected.
 *
 * Presentational: StockOutForm owns the list (fetchWastageEvents for the
 * wastage date), its load state, and the chosen id. This renders one of four
 * states — loading, failed, empty (with how to record a banquet), or the
 * select — plus the amber month-boundary note once an event is chosen.
 *
 * `onChange` receives the chosen event id as a NUMBER, or null when the user
 * goes back to the placeholder.
 */

const boxStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  padding: '0.75rem 1rem',
  boxSizing: 'border-box',
  width: '100%',
};

const noteStyle = {
  fontSize: '0.8rem',
  lineHeight: 1.45,
  borderRadius: '8px',
  padding: '8px 12px',
  marginTop: '8px',
};

const amberNote = { ...noteStyle, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a' };
const errorNote = { ...noteStyle, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca' };
const plainNote = { ...noteStyle, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb' };

const BanquetEventPicker = ({
  status = 'idle',
  events = [],
  range = null,
  error = null,
  value = null,
  onChange,
  wastageDate = null,
}) => {
  const selectId = useId();
  const list = Array.isArray(events) ? events : [];
  const chosen = findBanquetEvent(list, value);
  const note = chosen ? monthBoundaryNote(wastageDate, chosen.eventDate) : null;

  const handleChange = (e) => {
    const raw = e.target.value;
    const id = raw === '' ? null : Number(raw);
    if (typeof onChange === 'function') onChange(Number.isFinite(id) ? id : null);
  };

  let body;
  if (status === 'error') {
    body = (
      <div role="alert" style={errorNote}>
        {error || 'Could not load the banquets.'}
      </div>
    );
  } else if (list.length > 0) {
    body = (
      <select
        id={selectId}
        required
        aria-required="true"
        value={chosen ? String(chosen.eventId) : ''}
        onChange={handleChange}
        style={{
          width: '100%',
          marginTop: '6px',
          padding: '0.6rem 0.75rem',
          fontSize: '15px',
          color: '#000',
          backgroundColor: '#fff',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          boxSizing: 'border-box',
        }}
      >
        <option value="">Select the banquet…</option>
        {list.map((event) => (
          <option key={event.eventId} value={String(event.eventId)}>
            {formatEventOption(event)}
          </option>
        ))}
      </select>
    );
  } else if (status === 'ready') {
    body = (
      <div role="status" style={plainNote}>
        {emptyEventsMessage(range)}
      </div>
    );
  } else {
    body = (
      <div role="status" style={{ ...plainNote, color: '#6b7280' }}>
        Loading banquets…
      </div>
    );
  }

  return (
    <div className="banquet-event-picker" style={{ marginBottom: '8px' }}>
      <div style={boxStyle}>
        <label htmlFor={selectId} style={{ fontSize: '16px', fontWeight: 600, color: '#000' }}>
          Banquet event <span aria-hidden="true" style={{ color: '#dc2626' }}>*</span>
        </label>
        {body}
      </div>
      {note && (
        <div role="note" style={amberNote}>
          {note}
        </div>
      )}
    </div>
  );
};

export default BanquetEventPicker;
